import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useCheckPagePayment } from 'Src/feature/payment/useCheckPagePayment';

const TEST_ID = {
  root: `EvenSplitModal`,
  cta: {
    cancel: `EvenSplitModal-cancel`,
    next: `EvenSplitModal-next`,
  }
};

interface FormModel {
  splitBy: number;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

type EvenSplitModalControl = ReturnType<typeof useCheckPagePayment>['modal']['split'];

interface EvenSplitModalProps {
  control: EvenSplitModalControl;
}

const EvenSplitModalContent: FC<EvenSplitModalProps> = ({ control }) => {
  const { t } = useTranslation();

  const form = useForm<FormModel>({
    defaultValues: {
      splitBy: undefined,
    },
    schema: object().shape({
      splitBy: number()
        .label(t('labels.splitBy'))
        .transform((value: unknown) => Number.isNaN(value) ? null : value)
        .nullable()
        .required()
        .min(2)
        .max(100),
    }),
  });

  const { splitBy } = form.state.watch();

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={t('payment.labels.splitIntoParts')} />
      </ModalHeader>
      <ModalContent flex={{ alignItems: 'center', justifyContent: 'center' }}>
        <Form usedForm={form} omitChangeTracking>
          <Field type="numberInput" name="splitBy" />
        </Form>
      </ModalContent>
      <ModalActions>
        <ModalAction
          data-testid={TEST_ID.cta.cancel}
          label={t('labels.cancel')}
          color="secondary"
          onClick={() => control.close('on-user-cancel')}
        />
        <ModalAction
          data-testid={TEST_ID.cta.next}
          label={t('labels.next')}
          color="primary"
          onClick={() => control.onNext(parseInt(splitBy.toString()))}
          disabled={!form.state.isValid}
        />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const EvenSplitModal: FC<EvenSplitModalProps> = ({ control }) => (
  <Modal
    data-testid={TEST_ID.root}
    open={control.isOpen}
    onBackDropClick={() => control.close('on-user-cancel')}
  >
    <EvenSplitModalContent control={control} />
  </Modal>
);

export { EvenSplitModal, TEST_ID };
export type { EvenSplitModalProps };
